import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { searchArticles, fetchCategories, resolveImageUrl } from "../services/api";
import { Search, ArrowLeft } from "lucide-react";

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!query) return;
    setInput(query);
    setLoading(true);
    searchArticles(query)
      .then((data) => {
        setResults(data.articles || []);
        setSearched(true);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (input.trim().length >= 2) {
      setSearchParams({ q: input.trim() });
    }
  };

  const getCat = (id) => categories.find((c) => c.id === id);

  return (
    <div data-testid="search-results-page" className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/"
            data-testid="search-back-btn"
            className="p-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Search</h1>
        </div>

        {/* Search Input */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              data-testid="search-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Search by title, keyword, or topic..."
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all shadow-sm"
              autoFocus
            />
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#1B7A3D] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : searched && results.length === 0 ? (
          <div data-testid="search-no-results" className="text-center py-16">
            <Search size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No articles found for "<strong>{query}</strong>"</p>
            <p className="text-gray-400 text-xs mt-1">Try a different keyword or check the spelling</p>
          </div>
        ) : results.length > 0 ? (
          <div>
            <p className="text-xs text-gray-400 mb-4">
              {results.length} result{results.length !== 1 ? "s" : ""} for "<strong>{query}</strong>"
            </p>
            <div className="space-y-4">
              {results.map((article) => {
                const cat = getCat(article.category);
                const imgSrc = resolveImageUrl(article.image);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    data-testid={`search-result-${article.id}`}
                    className="group flex gap-4 bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-all"
                  >
                    {imgSrc && (
                      <div className="flex-shrink-0 w-32 sm:w-44 aspect-[4/3] overflow-hidden">
                        <img
                          src={imgSrc}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          style={{ objectPosition: `${article.image_focal_x ?? 50}% ${article.image_focal_y ?? 50}%` }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 py-3 pr-4">
                      {cat && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: cat.color || "#1B7A3D" }}
                        >
                          {cat.name}
                        </span>
                      )}
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5 group-hover:text-[#1B7A3D] transition-colors">
                        {article.title}
                      </h3>
                      {article.excerpt && (
                        <p className="text-xs text-gray-500 line-clamp-2 mt-1">{article.excerpt}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400">
                        {article.author?.name && <span>{article.author.name}</span>}
                        {article.date && <span>· {article.date}</span>}
                        {article.readTime && <span>· {article.readTime}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchResults;
